import React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Play, XOctagon, Terminal } from "lucide-react";

/**
 * Superadmin dev preview — shown before any Analyze / Estimate run when
 * the signed-in user is a superadmin. Renders the composed plugin
 * fragment (master + permanent + saved + adhoc prompts) exactly as it
 * will be appended to the system message, with Run (proceeds to submit)
 * and Abort (closes without running) buttons.
 *
 * Regular users never see this dialog — the parent component gates its
 * invocation on ``user.role === "superadmin"``.
 */
export default function PromptPreviewDialog({
    open,
    onOpenChange,
    fragment,
    kind,
    activePluginIds,
    onRun,
    onAbort,
    loading,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#0A1128] border-rose-500/40 text-slate-100 sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
                data-testid="prompt-preview-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        <Terminal className="w-4 h-4 text-rose-300" />
                        Prompt preview —{" "}
                        <span className="text-rose-300 uppercase tracking-wide">
                            {kind}
                        </span>
                    </DialogTitle>
                    <DialogDescription className="text-slate-400 text-[12px]">
                        Dev-only — shown to you because you're signed in as
                        superadmin. This is the plugin fragment that will be
                        appended to the system prompt{" "}
                        {(activePluginIds || []).length > 0 ? (
                            <>
                                for plugins{" "}
                                <span className="font-mono text-rose-300">
                                    {(activePluginIds || []).join(", ")}
                                </span>
                            </>
                        ) : (
                            "(no plugins active)"
                        )}
                        . The baseline system prompt + document corpus are
                        added on top by the backend and aren't shown here.
                    </DialogDescription>
                </DialogHeader>

                <div
                    className="flex-1 min-h-0 overflow-y-auto rounded-sm border border-[#2A3655] bg-black/40 p-3"
                    data-testid="prompt-preview-body"
                >
                    {fragment ? (
                        <pre className="text-[12px] leading-relaxed text-slate-200 whitespace-pre-wrap font-mono">
                            {fragment}
                        </pre>
                    ) : (
                        <div className="text-[12px] text-slate-500 italic">
                            (No plugin fragment — the run will use only the
                            baseline system prompt.)
                        </div>
                    )}
                </div>

                <DialogFooter className="pt-3 gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onAbort}
                        disabled={loading}
                        data-testid="prompt-preview-abort"
                        className="border-[#2A3655] text-slate-200 hover:bg-[#1C253C]"
                    >
                        <XOctagon className="w-3.5 h-3.5 mr-1" />
                        Abort
                    </Button>
                    <Button
                        type="button"
                        onClick={onRun}
                        disabled={loading}
                        data-testid="prompt-preview-run"
                        className="bg-emerald-500 hover:bg-emerald-600"
                    >
                        <Play className="w-3.5 h-3.5 mr-1" />
                        {loading ? "Running…" : "Run"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
